// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Financial tab of the Recommendations section: month/year-to-date savings
// KPIs (published by the realized-KPI listener as object-store attributes) over
// the daily settlement ledger derived from settled plan approvals.

import React, { useMemo, useState } from 'react';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Pagination,
} from '@carbon/react';
import { Calendar, Download } from '@carbon/icons-react';
import type { HitlApproval, SettlementLedgerRow, SiteFinancialSummary } from '../../types';
import { useSiteFinancials } from '../../hooks/useSiteFinancials';
import { EmptyState, Loading } from './ScreenHelpers';
import { useSiteTime } from '../../hooks/useSiteTime';
import { useUrlState } from '../../hooks/useUrlState';
import {
  buildSettlementLedger,
  defaultLedgerRange,
  filterLedger,
  formatLedgerDate,
  formatUsd,
  formatUsdEstimate,
  ledgerToCsv,
} from '../../utils/settlementLedger';
import './FinancialPanel.css';
import NoAccess from '../common/NoAccess';
import { isNoAccessMessage } from '../../services/api';

const PAGE_SIZES = [5, 10, 25, 50];

/**
 * Ledger columns. Every header sorts through Carbon's DataTable built-in sort:
 * cell values stay raw (ISO date, plain numbers) so the built-in comparison is
 * chronological and numeric; the display formatting happens at render time.
 */
const LEDGER_COLUMNS = [
  { key: 'date', header: 'Date', numeric: false },
  { key: 'approval', header: 'Plan approval', numeric: false },
  { key: 'status', header: 'Status', numeric: false },
  { key: 'expected', header: 'Expected', numeric: true },
  { key: 'savings', header: 'Savings', numeric: true },
];

/** Sort value for a figure the ledger doesn't have, so it groups at one end. */
const MISSING_FIGURE = Number.MIN_SAFE_INTEGER;

/** What the viewer reads in each cell, which is also what the search matches. */
function ledgerCellText(row: SettlementLedgerRow): string[] {
  return [
    formatLedgerDate(row.date),
    row.approved ? 'Approved' : 'Not approved',
    row.status === 'counted' ? 'Counted' : 'Unrealized',
    formatUsd(row.expectedUsd),
    formatUsdEstimate(row.realizedOrMissedUsd),
  ];
}

const isDateParam = (value: string) => value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value);

function formatPct(value: number | undefined): string {
  return value === undefined ? '—' : `${Math.round(value)}%`;
}

function days(count: number): string {
  return count === 1 ? '1 day' : `${count} days`;
}

/** Money earned reads green, money forfeited reads red; everything else stays neutral. */
type ValueTone = 'positive' | 'negative' | 'neutral';

function toneFor(value: number | undefined, tone: ValueTone): ValueTone {
  if (value === undefined || value === 0) return 'neutral';
  return value < 0 ? 'negative' : tone;
}

function toneClass(tone: ValueTone): string {
  return tone === 'neutral' ? '' : ` fin-value-${tone}`;
}

function KpiTile({
  label,
  value,
  note,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  note?: string;
  tone?: ValueTone;
}) {
  return (
    <div className="fin-kpi">
      <div className="fin-kpi-label">{label}</div>
      <div className={`fin-kpi-value${toneClass(tone)}`}>{value}</div>
      {note && <div className="fin-kpi-note">{note}</div>}
    </div>
  );
}

/** The four headline tiles, straight off the KPI attribute bag. */
function KpiRow({ summary }: { summary: SiteFinancialSummary }) {
  const approvedDays = summary.monthToDateApprovedDays;
  const unapprovedDays = summary.monthToDateUnapprovedDays;
  const ratedDays =
    approvedDays !== undefined && unapprovedDays !== undefined
      ? approvedDays + unapprovedDays
      : undefined;
  const missed = summary.monthToDateMissedSavingsUsd;

  return (
    <div className="fin-kpis">
      <KpiTile
        label="Approved savings (MTD)"
        value={formatUsd(summary.monthToDateRealizedSavingsUsd)}
        tone={toneFor(summary.monthToDateRealizedSavingsUsd, 'positive')}
        note={approvedDays === undefined ? undefined : `${days(approvedDays)} approved this month`}
      />
      <KpiTile
        label="Approved savings (YTD)"
        value={formatUsd(summary.yearToDateRealizedSavingsUsd)}
        tone={toneFor(summary.yearToDateRealizedSavingsUsd, 'positive')}
        note={summary.asOfDate ? `as of ${formatLedgerDate(summary.asOfDate)}` : undefined}
      />
      <KpiTile
        label="MTD approval rate"
        value={formatPct(summary.monthToDateApprovalRatePct)}
        note={
          ratedDays === undefined ? undefined : `${approvedDays} of ${ratedDays} days this month`
        }
      />
      <KpiTile
        label="Unrealized/Missed savings"
        value={formatUsd(missed)}
        tone={toneFor(missed, 'negative')}
        note={
          unapprovedDays === undefined
            ? undefined
            : `est. savings on ${days(unapprovedDays)} unapproved`
        }
      />
    </div>
  );
}

/** Approved / Not approved and Counted / Unrealized read as themed pills. */
function LedgerTag({ tone, children }: { tone: 'positive' | 'muted'; children: React.ReactNode }) {
  return <span className={`fin-tag fin-tag-${tone}`}>{children}</span>;
}

function DateRangeControl({
  from,
  to,
  spanLabel,
  onChange,
}: {
  from: string;
  to: string;
  spanLabel: string;
  onChange: (_next: { from: string; to: string }) => void;
}) {
  const { siteZone } = useSiteTime();
  const [open, setOpen] = useState(false);

  return (
    <div className="fin-range">
      <button
        type="button"
        className="fin-toolbar-btn"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <Calendar size={16} />
        {spanLabel}
      </button>
      {open && (
        <div className="fin-range-pop">
          <label className="fin-range-field">
            <span>From</span>
            <input
              type="date"
              value={from}
              onChange={e => onChange({ from: e.target.value, to })}
            />
          </label>
          <label className="fin-range-field">
            <span>To</span>
            <input type="date" value={to} onChange={e => onChange({ from, to: e.target.value })} />
          </label>
          <button
            type="button"
            className="fin-range-clear"
            onClick={() => {
              onChange(defaultLedgerRange(undefined, siteZone));
              setOpen(false);
            }}
          >
            Reset to this month
          </button>
        </div>
      )}
    </div>
  );
}

function rangeLabel(from: string, to: string): string {
  if (!from && !to) return 'All dates';
  if (!from) return `Through ${formatLedgerDate(to)}`;
  if (!to) return `From ${formatLedgerDate(from)}`;
  if (from === to) return formatLedgerDate(from);
  return `${formatLedgerDate(from)} – ${formatLedgerDate(to)}`;
}

export default function FinancialPanel({
  siteId,
  siteLabel,
  approvals,
}: {
  siteId: string;
  siteLabel?: string;
  approvals: HitlApproval[];
}) {
  const { summary, loading, error } = useSiteFinancials(siteId);
  const { siteZone } = useSiteTime();
  const [defaultRange] = useState(() => defaultLedgerRange(undefined, siteZone));
  const [urlState, patchUrl] = useUrlState({
    from: defaultRange.from,
    to: defaultRange.to,
    page: '1',
    size: String(PAGE_SIZES[0]),
  });
  const range = {
    from: isDateParam(urlState.from) ? urlState.from : defaultRange.from,
    to: isDateParam(urlState.to) ? urlState.to : defaultRange.to,
  };
  const page = Math.max(1, parseInt(urlState.page, 10) || 1);
  const pageSize = PAGE_SIZES.includes(Number(urlState.size))
    ? Number(urlState.size)
    : PAGE_SIZES[0];

  const rows = useMemo(() => buildSettlementLedger(approvals), [approvals]);
  const filtered = useMemo(
    () => filterLedger(rows, { from: range.from, to: range.to }),
    [rows, range.from, range.to]
  );
  const ledgerById = useMemo(() => new Map(filtered.map(row => [row.id, row])), [filtered]);
  const tableRows = useMemo(
    () =>
      filtered.map(row => ({
        id: row.id,
        date: row.date,
        approval: row.approved ? 'Approved' : 'Not approved',
        status: row.status === 'counted' ? 'Counted' : 'Unrealized',
        expected: row.expectedUsd ?? MISSING_FIGURE,
        savings: row.realizedOrMissedUsd ?? MISSING_FIGURE,
      })),
    [filtered]
  );
  // The search matches the text the viewer reads ("Aug 5, 2026", "$1,240"),
  // not the raw sort values the table holds.
  const filterLedgerRows = ({ rowIds, inputValue }: { rowIds: string[]; inputValue: string }) => {
    const query = inputValue.trim().toLowerCase();
    if (!query) return rowIds;
    return rowIds.filter(id => {
      const row = ledgerById.get(id);
      return row ? ledgerCellText(row).some(text => text.toLowerCase().includes(query)) : false;
    });
  };

  const handleExport = () => {
    if (typeof URL.createObjectURL !== 'function') return;
    const blob = new Blob([ledgerToCsv(filtered)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${(siteLabel || siteId || 'site').toLowerCase()}-daily-settlement.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fin-panel">
      {loading ? (
        <Loading label="Loading financials…" />
      ) : error && isNoAccessMessage(error) ? (
        <NoAccess big="You don't have access to financial metrics" />
      ) : error ? (
        <EmptyState big="Couldn't load financial metrics." sub={error} />
      ) : (
        <KpiRow summary={summary} />
      )}

      <TableContainer className="fin-ledger">
        <div className="fin-ledger-head">
          <h2 className="fin-ledger-title">Daily settlement ledger</h2>
          <div className="fin-ledger-actions" role="group" aria-label="Ledger actions">
            <DateRangeControl
              from={range.from}
              to={range.to}
              spanLabel={rangeLabel(range.from, range.to)}
              onChange={next => patchUrl({ from: next.from, to: next.to, page: null })}
            />
            <button type="button" className="fin-toolbar-btn" onClick={handleExport}>
              <Download size={16} />
              Export as CSV
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          rows.length === 0 ? (
            <EmptyState
              big="No settled days yet."
              sub="Days appear here once Energy Fluo has settled their realized or missed savings."
            />
          ) : (
            <EmptyState
              big="No days in this date range."
              sub="Widen or clear the range to see the rest of the ledger."
            />
          )
        ) : (
          <>
            <DataTable rows={tableRows} headers={LEDGER_COLUMNS} filterRows={filterLedgerRows}>
              {({
                rows: sortedRows,
                headers,
                getHeaderProps,
                getRowProps,
                getTableProps,
                onInputChange,
              }: any) => {
                const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
                const currentPage = Math.min(page, pageCount);
                const shown = sortedRows.slice(
                  (currentPage - 1) * pageSize,
                  currentPage * pageSize
                );
                return (
                  <>
                    <TableToolbar>
                      <TableToolbarContent>
                        <TableToolbarSearch
                          persistent
                          size="md"
                          labelText="Search the ledger"
                          placeholder="Search the ledger"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            onInputChange(event);
                            patchUrl({ page: null });
                          }}
                        />
                      </TableToolbarContent>
                    </TableToolbar>
                    {sortedRows.length === 0 ? (
                      <EmptyState
                        big="No days match your search."
                        sub="Clear the search to see every day in this date range."
                      />
                    ) : (
                      <Table {...getTableProps()} size="lg">
                        <TableHead>
                          <TableRow>
                            {headers.map(
                              (header: { key: string; header: string; numeric: boolean }) => (
                                <TableHeader
                                  {...getHeaderProps({
                                    header,
                                    isSortable: true,
                                    // A new order starts again from the first page.
                                    onClick: () => patchUrl({ page: null }),
                                  })}
                                  key={header.key}
                                  className={header.numeric ? 'fin-num' : undefined}
                                >
                                  {header.header}
                                </TableHeader>
                              )
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {shown.map((tableRow: { id: string }) => {
                            const row = ledgerById.get(tableRow.id);
                            if (!row) return null;
                            return (
                              <TableRow {...getRowProps({ row: tableRow })} key={row.id}>
                                <TableCell className="fin-date">
                                  {formatLedgerDate(row.date)}
                                </TableCell>
                                <TableCell>
                                  <LedgerTag tone={row.approved ? 'positive' : 'muted'}>
                                    {row.approved ? 'Approved' : 'Not approved'}
                                  </LedgerTag>
                                </TableCell>
                                <TableCell>
                                  <LedgerTag tone={row.status === 'counted' ? 'positive' : 'muted'}>
                                    {row.status === 'counted' ? 'Counted' : 'Unrealized'}
                                  </LedgerTag>
                                </TableCell>
                                <TableCell className="fin-num">
                                  {formatUsd(row.expectedUsd)}
                                </TableCell>
                                <TableCell
                                  className={`fin-num${toneClass(
                                    toneFor(
                                      row.realizedOrMissedUsd,
                                      row.approved ? 'positive' : 'negative'
                                    )
                                  )}`}
                                >
                                  {formatUsdEstimate(row.realizedOrMissedUsd)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                    {sortedRows.length > 0 && (
                      <Pagination
                        page={currentPage}
                        pageSize={pageSize}
                        pageSizes={PAGE_SIZES}
                        totalItems={sortedRows.length}
                        size="md"
                        onChange={({
                          page: nextPage,
                          pageSize: nextSize,
                        }: {
                          page: number;
                          pageSize: number;
                        }) => patchUrl({ page: String(nextPage), size: String(nextSize) })}
                      />
                    )}
                  </>
                );
              }}
            </DataTable>
          </>
        )}
      </TableContainer>
    </div>
  );
}
